import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Mail, Lock, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://revocut-production.up.railway.app';

const AdminResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [step, setStep] = useState(token ? 'reset' : 'request');
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);

  useEffect(() => {
    if (token) {
      verifyToken();
    }
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/verify-reset-token?token=${token}`);
      if (response.ok) {
        const data = await response.json();
        setTokenValid(true);
        setEmail(data.email);
      } else {
        setTokenValid(false);
        setError('Der Reset-Link ist ungültig oder abgelaufen.');
      }
    } catch (err) {
      setTokenValid(false);
      setError('Verbindungsfehler. Bitte versuche es später erneut.');
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      if (response.ok) {
        setStep('sent');
      } else {
        setError('Ein Fehler ist aufgetreten.');
      }
    } catch (err) {
      setError('Verbindungsfehler.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword })
      });
      
      if (response.ok) {
        setStep('success');
      } else {
        const data = await response.json();
        setError(data.detail || 'Ein Fehler ist aufgetreten.');
      }
    } catch (err) {
      setError('Verbindungsfehler.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Logo */}
      <div className="text-center mb-8">
        <Link to="/" className="inline-flex items-center">
          <img src="/revocut-logo.png" alt="RevoCut" className="w-9 h-9" />
          <span className="text-2xl font-bold text-white ml-1.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            REVOCUT
          </span>
        </Link>
      </div>

      <div className="w-full max-w-md">
        {/* Request Reset */}
        {step === 'request' && (
          <div className="p-8 rounded-2xl border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
            <h1 className="text-2xl font-bold text-white mb-2 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Admin Passwort vergessen?
            </h1>
            <p className="text-gray-400 text-center mb-6">
              Gib deine Admin E-Mail-Adresse ein.
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                  placeholder="admin@email.de"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                style={{ backgroundColor: '#5C63FF' }}
              >
                {loading ? 'Wird gesendet...' : 'Reset-Link senden'}
              </button>
            </form>

            <Link 
              to="/admin"
              className="mt-6 flex items-center justify-center gap-2 text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zum Login
            </Link>
          </div>
        )}

        {/* Email Sent */}
        {step === 'sent' && (
          <div className="p-8 rounded-2xl border border-white/10 text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
              <Mail className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">E-Mail gesendet!</h1>
            <p className="text-gray-400 mb-6">
              Falls ein Admin-Account mit <strong className="text-white">{email}</strong> existiert, 
              haben wir dir einen Reset-Link gesendet.
            </p>

            <Link 
              to="/admin"
              className="inline-flex items-center gap-2 text-purple-400 hover:text-purple-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Zurück zum Login
            </Link>
          </div>
        )}

        {/* Reset Password Form */}
        {step === 'reset' && (
          <div className="p-8 rounded-2xl border border-white/10" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
            {tokenValid === null ? (
              <div className="text-center">
                <div className="w-10 h-10 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-gray-400 mt-4">Link wird überprüft...</p>
              </div>
            ) : tokenValid === false ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)' }}>
                  <AlertCircle className="w-8 h-8 text-red-500" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Link ungültig</h1>
                <p className="text-gray-400 mb-6">{error}</p>
                <button
                  onClick={() => { setStep('request'); setError(''); }}
                  className="px-6 py-3 rounded-xl font-semibold text-white"
                  style={{ backgroundColor: '#5C63FF' }}
                >
                  Neuen Link anfordern
                </button>
              </div>
            ) : (
              <>
                <h1 className="text-2xl font-bold text-white mb-2 text-center">Neues Passwort</h1>
                <p className="text-gray-400 text-center mb-6">
                  Für: <strong className="text-white">{email}</strong>
                </p>

                {error && (
                  <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={6}
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder="Neues Passwort"
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 transition-colors"
                      placeholder="Passwort bestätigen"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 rounded-xl font-semibold text-white transition-all hover:scale-[1.02] disabled:opacity-50"
                    style={{ backgroundColor: '#5C63FF' }}
                  >
                    {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
                  </button>
                </form>
              </>
            )}
          </div>
        )}

        {/* Success */}
        {step === 'success' && (
          <div className="p-8 rounded-2xl border border-white/10 text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)' }}>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-white mb-2">Passwort geändert!</h1>
            <p className="text-gray-400 mb-6">Du kannst dich jetzt mit deinem neuen Passwort einloggen.</p>

            <Link 
              to="/admin"
              className="inline-block w-full py-4 rounded-xl font-semibold text-white text-center"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Zum Admin Login
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminResetPassword;
